import React, { useState, useEffect } from 'react';
import { Button, Modal, Toast } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Storage } from 'aws-amplify';
import { useAuth } from 'app/context/auth-context';
import { VJFPUserService } from 'app/services/VJFPUserService';
import { VJFPUser } from 'app/API';

// Add this helper function at the top of the file
const generateStrongPassword = () => {
    const length = 12;
    const numbers = "0123456789";
    const special = "!@#$%^&*";
    const letters = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    
    // Ensure at least one of each required type
    let password = numbers.charAt(Math.floor(Math.random() * numbers.length)) +
                   special.charAt(Math.floor(Math.random() * special.length));
    
    // Fill the rest with random characters from all sets
    const charset = letters + numbers + special;
    for (let i = password.length; i < length; i++) {
        password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    // Shuffle the password to avoid predictable positions
    return password.split('').sort(() => Math.random() - 0.5).join('');
};

const ManageUsers: React.FC<{ organizationId: string }> = ({ organizationId }: { organizationId: string }) => {
    const [users, setUsers] = useState<VJFPUser[]>([]);
    const [showModal, setShowModal] = useState(false);
    const { user: currentUser } = useAuth();
    const vjfpUserService = new VJFPUserService();
    // const [showGroupModal, setShowGroupModal] = useState(false);
    // const [groups, setGroups] = useState<VJFPUserGroup[]>([]);
    const [selectedUser, setSelectedUser] = useState<VJFPUser | null>(null);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState<VJFPUser | null>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showToast, setShowToast] = useState(false);


    const fetchUsers = async () => {
        if (currentUser?.id) {
            const fetchedUsers = await vjfpUserService.getUsers(currentUser.organizationId);
            setUsers(fetchedUsers);
        }
    };

    useEffect(() => {
        // const fetchGroups = async () => {
        //     const fetchedGroups = await vjfpUserService.listGroups(organizationId);
        //     setGroups(fetchedGroups);
        // };
        // fetchGroups();
        fetchUsers();

    }, []);


    // Update modal open handler
    const handleModalOpen = (user?: VJFPUser) => {
        if (user) {
            setSelectedUser(user);
            setModalMode('edit');
            formik.setValues({
                firstName: user.firstName,
                lastName: user.lastName || '',
                email: user.emailId,
                avatar: null,
                // group: user.groupId || '',
                tempPassword: generateStrongPassword(),
            });
        } else {
            setSelectedUser(null);
            setModalMode('create');
            formik.resetForm();
        }
        setShowModal(true);
    };


    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            avatar: null as File | null,
            // group: '',
            tempPassword: generateStrongPassword(),
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required('First Name is required'),
            lastName: Yup.string().required('Last Name is required'),
            email: Yup.string().email('Invalid email address').required('Email is required'),
            avatar: Yup.mixed(), // Make avatar optional
            // group: Yup.string().required('Group selection is required'),
            tempPassword: Yup.string().required('Temporary Password is required'),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!currentUser?.id) return;
            try {
                if (modalMode === 'create') {
                    const newUserId = await vjfpUserService.createOrgUser(organizationId, {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        emailId: values.email,
                        organizationId: currentUser.organizationId,
                        roles: ['Recruiter'],
                    }, values.tempPassword);

                    // if (values.group) {
                    //     await vjfpUserService.addUserToGroup(organizationId, newUserId, values.group);
                    // }

                    if (values.avatar) {
                        const avatarKey = `${currentUser.organizationId}/users/${newUserId}/avatar.png`;
                        await Storage.put(avatarKey, values.avatar, {
                            contentType: values.avatar.type,
                        });
                    }
                } else if (selectedUser) {

                    // if (values.group !== selectedUser.groupId) {
                    //     await vjfpUserService.addUserToGroup(organizationId, selectedUser.id, values.group);
                    // }

                    if (values.avatar) {
                        const avatarKey = `${currentUser.organizationId}/users/${selectedUser.id}/avatar.png`;
                        await Storage.put(avatarKey, values.avatar, {
                            contentType: values.avatar.type,
                        });
                    }
                }

                setShowModal(false);
                resetForm();
                fetchUsers();
            } catch (error) {
                console.error('Error managing user:', error);
            }
        },
    });

    const handleDeleteUser = (user: VJFPUser) => {
        if (user.id === currentUser?.id) {
            alert("You cannot delete your own account");
            return;
        }
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;

        try {
            //await vjfpUserService.deleteUser(organizationId, userToDelete.id);
            alert('User deleted successfully');
            fetchUsers();
        } catch (error) {
            alert('Failed to delete user');
            console.error('Error deleting user:', error);
        }
        setShowDeleteModal(false);
    };

    return (
        <div className="card">
            <div className="card-header border-0 pt-6">
                <div className="card-title">
                    <div className="d-flex align-items-center position-relative my-1">
                        <i className="ki-duotone ki-magnifier fs-1 position-absolute ms-6">
                            <span className="path1"></span>
                            <span className="path2"></span>
                        </i>
                        <input
                            type="text"
                            data-kt-user-table-filter="search"
                            className="form-control form-control-solid w-250px ps-14"
                            placeholder="Search user"
                        />
                    </div>
                </div>
                <div className="card-toolbar">
                    <div className="d-flex justify-content-end" data-kt-user-table-toolbar="base">
                        {/* <Button variant='secondary' className='me-5' onClick={() => setShowGroupModal(true)}>Manage Groups</Button> */}
                        <Button onClick={() => {setModalMode('create'); setShowModal(true)}}>Add recruiter</Button>
                    </div>
                </div>
            </div>
            <div className="card-body py-4">
                {users?.length ? (
                    <div className="table-responsive">
                        <table className="table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer">
                            <thead>
                                <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                    <th className="w-50px">Photo</th>
                                    <th className="min-w-125px">First Name</th>
                                    <th className="min-w-125px">Last Name</th>
                                    <th className="min-w-125px">Email</th>
                                    <th className="min-w-125px">Role</th>
                                    <th className="text-end min-w-100px">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="fw-semibold text-gray-600">
                                {users.map((user) => (
                                    <tr key={user.id}>
                                        <td>
                                            <div className="symbol symbol-45px me-5">
                                                {user.avatar ? (
                                                    <img src={user.avatar} alt={`${user.firstName} ${user.lastName}`} />
                                                ) : (
                                                    <span className="symbol-label bg-light-primary text-primary fw-bolder">
                                                        {user.firstName.charAt(0).toUpperCase()}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>{user.firstName}</td>
                                        <td>{user.lastName}</td>
                                        <td>
                                            <a href={`mailto:${user.emailId}`} className="text-gray-600 text-hover-primary">
                                                {user.emailId}
                                            </a>
                                        </td>
                                        <td>
                                            <span className="badge badge-light-primary">{user.roles?.join(', ')}</span>
                                        </td>
                                        <td className="text-end">
                                            <button
                                                className="btn btn-icon btn-light-primary btn-active-light-primary btn-sm me-2"
                                                onClick={() => handleModalOpen(user)}
                                            >
                                                <i className="bi bi-pencil fs-4"></i>
                                            </button>

                                            <button
                                                className="btn btn-icon btn-light-danger btn-active-light-danger btn-sm"
                                                onClick={() => handleDeleteUser(user)}
                                            >
                                                <i className="bi bi-trash fs-4"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="card-px text-center py-20 my-10">
                        <h2 className="fs-2x fw-bold mb-10">No Recruiters Added Yet</h2>
                        <p className="text-gray-400 fs-4 fw-semibold mb-10">
                            Onboard Recruiters to your organisation
                        </p>
                        <Button variant="primary" onClick={() => {setModalMode('create'); setShowModal(true)}}>
                            Add Recruiter(s)
                        </Button>
                    </div>
                )}
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{modalMode === 'create' ? 'Add New recruiter' : 'Edit recruiter'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="firstName" className="form-label">First Name</label>
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                className={`form-control ${formik.touched.firstName && formik.errors.firstName ? 'is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.firstName}
                            />
                            {formik.touched.firstName && formik.errors.firstName && (
                                <div className="invalid-feedback">{formik.errors.firstName}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="lastName" className="form-label">Last Name</label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                className={`form-control ${formik.touched.lastName && formik.errors.lastName ? 'is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.lastName}
                            />
                            {formik.touched.lastName && formik.errors.lastName && (
                                <div className="invalid-feedback">{formik.errors.lastName}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="email" className="form-label">Email</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                className={`form-control ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                            />
                            {formik.touched.email && formik.errors.email && (
                                <div className="invalid-feedback">{formik.errors.email}</div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="avatar" className="form-label">Photo</label>
                            <input
                                id="avatar"
                                name="avatar"
                                type="file"
                                accept="image/*"
                                className={`form-control ${formik.touched.avatar && formik.errors.avatar ? 'is-invalid' : ''}`}
                                onChange={(event) => {
                                    formik.setFieldValue("avatar", event.currentTarget.files?.[0]);
                                }}
                            />
                            {formik.touched.avatar && formik.errors.avatar && (
                                <div className="invalid-feedback">{formik.errors.avatar}</div>
                            )}
                        </div>
                        {/* <div className="mb-3">
                            <label className="required form-label">Group</label>
                            <select
                                name="group"
                                className="form-select form-select-solid"
                                data-control="select2"
                                data-placeholder="Select a group"
                                onChange={formik.handleChange}
                                value={formik.values.group}
                            >
                                <option value="">Select group</option>
                                {groups.map((group) => (
                                    <option key={group.groupId} value={group.groupId}>
                                        {group.name}
                                    </option>
                                ))}
                            </select>
                            {formik.touched.group && formik.errors.group && (
                                <div className="invalid-feedback d-block">{formik.errors.group}</div>
                            )}
                        </div> */}
                        {modalMode === 'create' && (
                            <div className="mb-3 position-relative">
                                <label htmlFor="tempPassword" className="form-label">Temporary Password</label>
                                <div className="input-group">
                                    <input
                                        id="tempPassword"
                                        name="tempPassword"
                                        type={showPassword ? "text" : "password"}
                                        className="form-control"
                                        value={formik.values.tempPassword}
                                        readOnly
                                    />
                                    <button
                                        className="btn btn-secondary"
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(formik.values.tempPassword);
                                            setShowToast(true);
                                        }}
                                    >
                                        <i className="bi bi-clipboard"></i>
                                    </button>
                                </div>
                                <small className="text-muted">
                                    This is a generated strong password for the new user's initial login
                                </small>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Role</label>
                            <input
                                type="text"
                                className="form-control"
                                value="Recruiter"
                                disabled
                            />
                        </div>
                        <button type="submit" className="btn btn-primary">
                            {modalMode === 'create' ? 'Create User' : 'Update User'}
                        </button>
                    </form>
                </Modal.Body>
            </Modal>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {userToDelete && (
                        <div>
                            <p className="fw-bold text-gray-800 fs-6">
                                This will permanently delete {userToDelete.firstName} {userToDelete.lastName}. This action cannot be undone.
                            </p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* <ManageUserGroups
                show={showGroupModal}
                onHide={() => setShowGroupModal(false)}
                organizationId={organizationId}
            /> */}
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                style={{
                    position: 'fixed',
                    bottom: 20,
                    right: 20,
                    zIndex: 9999
                }}
                delay={3000}
                autohide={true}
            >
                <Toast.Header>
                    <strong className="me-auto">Success</strong>
                </Toast.Header>
                <Toast.Body>Password copied to clipboard!</Toast.Body>
            </Toast>
        </div>
    );
};

export default ManageUsers;
